// /web/src/pages/Pricing.jsx
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import PricingTierCards from "../components/PricingTierCards.jsx";
import { pricingTiers } from "../data/pricingTiers.js";
import { useBilling } from "../lib/api.js";

export default function Pricing() {
  const { search } = useLocation();
  const reason = useMemo(() => new URLSearchParams(search).get("reason"), [search]);
  const planFromQuery = useMemo(() => new URLSearchParams(search).get("plan"), [search]);
  const billing = useBilling();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const autoCheckoutRef = useRef(false);

  const handleTierSelect = useCallback(
    async (tier) => {
      if (!tier?.plan) return;

      if (!isAuthenticated) {
        try {
          await loginWithRedirect({
            authorizationParams: { screen_hint: "signup" },
            appState: { returnTo: `/pricing?plan=${tier.plan}` },
          });
        } catch (error) {
          console.error("Login required before starting checkout", error);
          alert("Please sign in to continue to checkout.");
        }
        return;
      }

      try {
        autoCheckoutRef.current = true;
        await billing.startPlanCheckout(tier.plan);
      } catch (error) {
        console.error("Unable to start checkout", error);
        alert(error?.message || "Could not start checkout. Please try again.");
      }
    },
    [billing, isAuthenticated, loginWithRedirect]
  );

  useEffect(() => {
    if (!planFromQuery || !isAuthenticated || autoCheckoutRef.current) {
      return;
    }

    const tier = pricingTiers.find((t) => t.plan === planFromQuery);
    if (!tier) {
      return;
    }

    autoCheckoutRef.current = true;
    handleTierSelect(tier);
  }, [planFromQuery, isAuthenticated, handleTierSelect]);

  return (
    <>
      <section className="pd-section">
        <div className="pd-container">
          <header className="pd-pricing-page__intro">
            <h1 className="pd-h2">Pricing</h1>
            <p className="pd-lead">Simple flat-rate subscriptions as you grow.</p>
          </header>
        </div>
      </section>

      <section className="pd-section">
        <div className="pd-container pd-pricing-page">
          {reason === "out_of_tokens" && (
            <div className="pd-pricing-page__alert" role="alert">
              Your current plan no longer covers access. Choose a subscription to continue, or visit Billing to
              manage your subscription.
            </div>
          )}

          <PricingTierCards tiers={pricingTiers} onTierSelect={handleTierSelect} />

          <p className="pd-caption pd-pricing-page__caption">All prices exclude VAT.</p>
        </div>
      </section>
    </>
  );
}
